import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import API from "./config/API";

function AllAvailableBeds() {
  const navigate = useNavigate();
  const location = useLocation();

  const adminEmailId = location.state?.adminemailId;

  console.log("Admin Email Id:", adminEmailId);

  const [beds, setBeds] = useState([]);
  const [filteredBeds, setFilteredBeds] = useState([]);
  const [loading, setLoading] = useState(true);

  // =====================================================
  // FILTER STATES
  // =====================================================

  const [selectedFloor, setSelectedFloor] = useState("");
  const [selectedRoom, setSelectedRoom] = useState("");
  const [selectedCost, setSelectedCost] = useState("");

  const [selectedSnoringRoom, setSelectedSnoringRoom] = useState("");

  // IMPORTANT
  // "" = All Bed Sharing
  // "1" = 1 Sharing
  // "2" = 2 Sharing
  // etc.
  const [bedSharingFilter, setBedSharingFilter] = useState("");

  // =====================================================
  // FETCH AVAILABLE BEDS
  // =====================================================

  useEffect(() => {
    fetchAvailableBeds();
  }, []);

  const fetchAvailableBeds = async () => {
    try {
      setLoading(true);

      const user = JSON.parse(localStorage.getItem("user"));

      const payload = {
        emailId: adminEmailId || user?.emailId,
      };

      console.log("Available Beds Payload:", payload);

      const res = await fetch(`${API.V2}/pgOwner/getAllAvailableBedsinPg`, {
        method: "POST",

        headers: {
          "Content-Type": "application/json",

          Authorization: `Bearer ${user?.jwtToken || ""}`,
        },

        body: JSON.stringify(payload),
      });

      const data = await res.json();

      console.log("Available Beds Response:", data);

      if (data.statusCode === 200) {
        const uniqueBeds = Array.from(
          new Map((data.data || []).map((item) => [item.bedId, item])).values(),
        );

        setBeds(uniqueBeds);
        setFilteredBeds(uniqueBeds);
      } else if (data.statusCode === 401) {
        alert("Session expired. Please login again.");

        localStorage.clear();

        navigate("/");
      } else {
        setBeds([]);
        setFilteredBeds([]);
      }
    } catch (error) {
      console.error("Error fetching available beds:", error);

      setBeds([]);
      setFilteredBeds([]);
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // BED SHARING OPTIONS
  //
  // Automatically gets sharing values from API.
  //
  // Example API:
  //
  // bedSharingPerRoom: 1
  // bedSharingPerRoom: 2
  // bedSharingPerRoom: 3
  //
  // Dropdown:
  //
  // All Bed Sharing
  // 1 Sharing
  // 2 Sharing
  // 3 Sharing
  // =====================================================

  const bedSharingOptions = useMemo(() => {
    const sharingValues = [
      ...new Set(
        beds
          .map((bed) => bed?.bedSharingPerRoom)
          .filter(
            (value) => value !== null && value !== undefined && value !== "",
          ),
      ),
    ];

    return sharingValues.sort((a, b) => Number(a) - Number(b));
  }, [beds]);

  // =====================================================
  // FILTER
  //
  // ALL FILTERS WORK TOGETHER
  //
  // Floor
  // AND Room
  // AND Bed Sharing
  // AND Cost
  // AND Snoring Room
  // =====================================================

  useEffect(() => {
    let filtered = [...beds];

    // ===================================================
    // FLOOR FILTER
    // ===================================================

    if (selectedFloor) {
      filtered = filtered.filter(
        (bed) => String(bed.floorName) === String(selectedFloor),
      );
    }

    // ===================================================
    // ROOM FILTER
    // ===================================================

    if (selectedRoom) {
      filtered = filtered.filter(
        (bed) => String(bed.roomName) === String(selectedRoom),
      );
    }

    // ===================================================
    // BED SHARING FILTER
    //
    // THIS IS THE IMPORTANT PART
    //
    // Example:
    //
    // bedSharingFilter = "2"
    //
    // only:
    //
    // bed.bedSharingPerRoom = 2
    // ===================================================

    if (bedSharingFilter && bedSharingFilter !== "ALL") {
      filtered = filtered.filter(
        (bed) => Number(bed.bedSharingPerRoom) === Number(bedSharingFilter),
      );
    }

    // ===================================================
    // COST FILTER
    // ===================================================

    if (selectedCost) {
      filtered = filtered.filter(
        (bed) => String(bed.bedCost) === String(selectedCost),
      );
    }

    // ===================================================
    // SNORING ROOM FILTER
    // ===================================================

    if (selectedSnoringRoom) {
      filtered = filtered.filter(
        (bed) =>
          String(bed.roomName) === String(selectedSnoringRoom) &&
          (bed.isSnoringroom === true || bed.isSnoringroom === "true"),
      );
    }

    // ===================================================
    // SET FINAL FILTERED DATA
    // ===================================================

    setFilteredBeds(filtered);
  }, [
    selectedFloor,
    selectedRoom,
    selectedCost,
    selectedSnoringRoom,
    bedSharingFilter,
    beds,
  ]);

  // =====================================================
  // FLOOR DROPDOWN
  // =====================================================

  const floors = useMemo(() => {
    return [
      ...new Set(
        beds
          .map((bed) => bed.floorName)
          .filter(
            (value) => value !== null && value !== undefined && value !== "",
          ),
      ),
    ];
  }, [beds]);

  // =====================================================
  // ROOM DROPDOWN
  //
  // Rooms depend on selected floor
  // =====================================================

  const rooms = useMemo(() => {
    return [
      ...new Set(
        beds
          .filter((bed) =>
            selectedFloor
              ? String(bed.floorName) === String(selectedFloor)
              : true,
          )
          .map((bed) => bed.roomName)
          .filter(
            (value) => value !== null && value !== undefined && value !== "",
          ),
      ),
    ];
  }, [beds, selectedFloor]);

  // =====================================================
  // COST DROPDOWN
  //
  // Costs depend on selected room
  // and selected floor
  // =====================================================

  const costs = useMemo(() => {
    return [
      ...new Set(
        beds
          .filter((bed) => {
            const floorMatch = selectedFloor
              ? String(bed.floorName) === String(selectedFloor)
              : true;

            const roomMatch = selectedRoom
              ? String(bed.roomName) === String(selectedRoom)
              : true;

            return floorMatch && roomMatch;
          })
          .map((bed) => bed.bedCost)
          .filter(
            (value) => value !== null && value !== undefined && value !== "",
          ),
      ),
    ].sort((a, b) => Number(a) - Number(b));
  }, [beds, selectedFloor, selectedRoom]);

  // =====================================================
  // SNORING ROOMS
  // =====================================================

  const snoringRooms = useMemo(() => {
    return [
      ...new Set(
        beds
          .filter((bed) => {
            const floorMatch = selectedFloor
              ? String(bed.floorName) === String(selectedFloor)
              : true;

            const roomMatch = selectedRoom
              ? String(bed.roomName) === String(selectedRoom)
              : true;

            const snoringMatch =
              bed.isSnoringroom === true || bed.isSnoringroom === "true";

            return floorMatch && roomMatch && snoringMatch;
          })
          .map((bed) => bed.roomName),
      ),
    ];
  }, [beds, selectedFloor, selectedRoom]);

  // =====================================================
  // PG NAME
  // =====================================================

  const pgName = localStorage.getItem("pgName") || "";

  // =====================================================
  // NAVIGATION
  // =====================================================

  const handleNavigate = (bed, type) => {
    const bookingData = {
      available: bed.available,

      bedCost: bed.bedCost,

      bedofferPrice: bed.offerPrice,

      bedCostPerDay: bed.bedCostPerDay,

      bedId: bed.bedId,

      bedName: bed.bedName,

      floorId: bed.floorId,

      floorName: bed.floorName,

      isSnoringroom: bed.isSnoringroom,

      roomId: bed.roomId,

      roomName: bed.roomName,

      // IMPORTANT
      bedSharingPerRoom: bed.bedSharingPerRoom,

      pgName: pgName,
    };

    navigate(
      type === "view" ? "/availableBedViewDetails" : "/UserCompleteBookingForm",
      {
        state: bookingData,
      },
    );
  };

  // =====================================================
  // RESET FILTERS
  // =====================================================

  const handleResetFilters = () => {
    setSelectedFloor("");
    setSelectedRoom("");
    setSelectedCost("");
    setSelectedSnoringRoom("");

    // IMPORTANT
    setBedSharingFilter("");
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div style={styles.page}>
      {/* =================================================
          HEADER
      ================================================= */}

      <div style={styles.header}>
        <h1 style={styles.heading}>Available Beds in {pgName} PG 🏠</h1>

        <p style={styles.subHeading}>Choose your preferred room and bed</p>
      </div>

      {/* =================================================
          FILTERS
      ================================================= */}

      <div style={styles.filterContainer}>
        {/* ===============================================
            FLOOR
        =============================================== */}

        <select
          value={selectedFloor}
          onChange={(e) => {
            setSelectedFloor(e.target.value);

            setSelectedRoom("");
            setSelectedCost("");
            setSelectedSnoringRoom("");
          }}
          style={styles.select}
        >
          <option value="">All Floors</option>

          {floors.map((floor, i) => (
            <option key={i} value={floor}>
              {floor}
            </option>
          ))}
        </select>

        {/* ===============================================
            ROOM
        =============================================== */}

        <select
          value={selectedRoom}
          onChange={(e) => {
            setSelectedRoom(e.target.value);

            setSelectedCost("");
            setSelectedSnoringRoom("");
          }}
          style={styles.select}
        >
          <option value="">All Rooms</option>

          {rooms.map((room, i) => (
            <option key={i} value={room}>
              {room}
            </option>
          ))}
        </select>

        {/* ===============================================
            BED SHARING
        =============================================== */}

        <select
          value={bedSharingFilter}
          onChange={(e) => setBedSharingFilter(e.target.value)}
          style={styles.select}
        >
          <option value="">All Bed Sharing</option>

          {bedSharingOptions.map((sharing, i) => (
            <option key={i} value={sharing}>
              {sharing} Sharing
            </option>
          ))}
        </select>

        {/* ===============================================
            COST
        =============================================== */}

        <select
          value={selectedCost}
          onChange={(e) => setSelectedCost(e.target.value)}
          style={styles.select}
        >
          <option value="">All Costs</option>

          {costs.map((cost, i) => (
            <option key={i} value={cost}>
              ₹ {cost}
            </option>
          ))}
        </select>

        {/* ===============================================
            SNORING ROOMS
        =============================================== */}

        <select
          value={selectedSnoringRoom}
          onChange={(e) => setSelectedSnoringRoom(e.target.value)}
          style={styles.select}
        >
          <option value="">😴 Snoring Rooms</option>

          {snoringRooms.map((room, i) => (
            <option key={i} value={room}>
              😴 {room}
            </option>
          ))}
        </select>

        {/* ===============================================
            RESET
        =============================================== */}

        <button
          type="button"
          onClick={handleResetFilters}
          style={styles.resetButton}
        >
          Reset
        </button>
      </div>

      {/* =================================================
          LOADING
      ================================================= */}

      {loading ? (
        <div style={styles.loaderContainer}>
          <div style={styles.loader}></div>

          <p>Loading Available Beds...</p>
        </div>
      ) : (
        <>
          {/* =============================================
              COUNT
          ============================================= */}

          <div style={styles.countBox}>
            Total Available Beds :
            <span style={styles.countNumber}> {filteredBeds.length}</span>
          </div>

          {/* =============================================
              CARDS
          ============================================= */}

          <div style={styles.cardContainer}>
            {filteredBeds.length > 0 ? (
              filteredBeds.map((bed) => (
                <div key={bed.bedId} style={styles.card}>
                  {/* =================================
                        CARD TOP
                    ================================= */}

                  <div style={styles.cardTop}>
                    <div>
                      <h2 style={styles.bedName}>{bed.bedName}</h2>

                      <p style={styles.roomText}>🚪 {bed.roomName}</p>

                      <p style={styles.floorText}>🏢 {bed.floorName}</p>

                      {/* =============================
                            BED SHARING
                        ============================= */}

                      <div style={styles.infoRow}>
                        <span>Bed Sharing</span>

                        <strong style={styles.sharingBadge}>
                          {bed.bedSharingPerRoom} Sharing
                        </strong>
                      </div>
                    </div>

                    <div style={styles.availableBadge}>Available</div>
                  </div>

                  {/* =================================
                        CARD MIDDLE
                    ================================= */}

                  <div style={styles.cardMiddle}>
                    {/* MONTHLY PRICE */}

                    <div style={styles.priceSection}>
                      {bed.inOfferPrice_bedCost > 0 ? (
                        <>
                          <span style={styles.oldPrice}>₹ {bed.bedCost}</span>

                          <span style={styles.offerPrice}>
                            ₹ {bed.inOfferPrice_bedCost}
                          </span>

                          <span style={styles.monthText}> / month</span>
                        </>
                      ) : (
                        <>
                          <span style={styles.price}>₹ {bed.bedCost}</span>

                          <span style={styles.monthText}> / month</span>
                        </>
                      )}
                    </div>

                    {/* PER DAY */}

                    <div style={styles.perDayBox}>
                      ☀ Per Day :
                      <span style={styles.perDayPrice}>
                        ₹ {bed.bedCostPerDay}
                      </span>
                    </div>
                  </div>

                  {/* =================================
                        CARD BOTTOM
                    ================================= */}

                  <div style={styles.cardBottom}>
                    <button
                      style={styles.viewBtn}
                      onClick={() => handleNavigate(bed, "view")}
                    >
                      View Details
                    </button>

                    <button
                      style={styles.bookBtn}
                      onClick={() => handleNavigate(bed, "book")}
                    >
                      Book Now
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div style={styles.noData}>No Beds Available</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default AllAvailableBeds;

// =========================================================
// STYLES
// =========================================================

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f5f7fb",
    padding: "25px",
    fontFamily: "Segoe UI, sans-serif",
  },

  header: {
    textAlign: "center",
    marginBottom: "30px",
  },

  heading: {
    fontSize: "38px",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "8px",
  },

  subHeading: {
    color: "#64748b",
    fontSize: "17px",
  },

  // =======================================================
  // FILTERS
  // =======================================================

  filterContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "18px",
    marginBottom: "30px",
  },

  select: {
    padding: "12px 16px",
    minWidth: "220px",
    borderRadius: "10px",
    border: "1px solid #dbeafe",
    background: "#fff",
    fontSize: "15px",
    outline: "none",
    cursor: "pointer",
    fontWeight: "600",
  },

  resetButton: {
    padding: "12px 20px",
    borderRadius: "10px",
    border: "none",
    background: "#475569",
    color: "#fff",
    fontSize: "15px",
    fontWeight: "700",
    cursor: "pointer",
  },

  // =======================================================
  // COUNT
  // =======================================================

  countBox: {
    textAlign: "center",
    fontSize: "20px",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "25px",
  },

  countNumber: {
    color: "#2563eb",
  },

  // =======================================================
  // GRID
  // =======================================================

  cardContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 300px)",
    justifyContent: "center",
    gap: "22px",
    width: "100%",
  },

  // =======================================================
  // CARD
  // =======================================================

  card: {
    width: "300px",
    minHeight: "285px",
    background: "#fff",
    borderRadius: "16px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 6px 18px rgba(0,0,0,.08)",
    padding: "18px",
    transition: "0.3s",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    cursor: "pointer",
  },

  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "15px",
  },

  bedName: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "700",
    color: "#0f172a",
  },

  roomText: {
    margin: "8px 0 4px",
    color: "#475569",
    fontWeight: "600",
    fontSize: "14px",
  },

  floorText: {
    margin: 0,
    color: "#64748b",
    fontSize: "14px",
  },

  // =======================================================
  // SHARING BADGE
  // =======================================================

  sharingBadge: {
    background: "#dbeafe",
    color: "#1d4ed8",
    padding: "5px 10px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "700",
  },

  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    marginTop: "10px",
    fontSize: "14px",
  },

  availableBadge: {
    background: "#dcfce7",
    color: "#15803d",
    borderRadius: "30px",
    padding: "6px 12px",
    fontWeight: "700",
    fontSize: "12px",
  },

  // =======================================================
  // PRICE
  // =======================================================

  cardMiddle: {
    borderTop: "1px solid #e2e8f0",
    borderBottom: "1px solid #e2e8f0",
    padding: "15px 0",
    marginBottom: "15px",
  },

  priceSection: {
    marginBottom: "10px",
  },

  oldPrice: {
    fontSize: "18px",
    color: "#94a3b8",
    textDecoration: "line-through",
    marginRight: "10px",
    fontWeight: "600",
  },

  offerPrice: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#16a34a",
  },

  price: {
    fontSize: "28px",
    color: "#ef4444",
    fontWeight: "700",
  },

  monthText: {
    color: "#64748b",
    fontWeight: "600",
    fontSize: "13px",
  },

  perDayBox: {
    marginTop: "10px",
    background: "#eff6ff",
    color: "#1d4ed8",
    borderRadius: "8px",
    padding: "8px 12px",
    display: "inline-block",
    fontWeight: "600",
    fontSize: "14px",
  },

  perDayPrice: {
    marginLeft: "8px",
    color: "#dc2626",
    fontWeight: "700",
    fontSize: "16px",
  },

  // =======================================================
  // BUTTONS
  // =======================================================

  cardBottom: {
    display: "flex",
    gap: "10px",
  },

  viewBtn: {
    flex: 1,
    padding: "10px",
    borderRadius: "8px",
    border: "2px solid #2563eb",
    background: "#fff",
    color: "#2563eb",
    fontWeight: "700",
    cursor: "pointer",
    fontSize: "14px",
  },

  bookBtn: {
    flex: 1,
    padding: "10px",
    borderRadius: "8px",
    border: "none",
    background: "linear-gradient(45deg,#ef4444,#f97316)",
    color: "#fff",
    fontWeight: "700",
    cursor: "pointer",
    fontSize: "14px",
    boxShadow: "0 5px 15px rgba(239,68,68,.25)",
  },

  // =======================================================
  // LOADER
  // =======================================================

  loaderContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "100px",
  },

  loader: {
    width: "55px",
    height: "55px",
    border: "5px solid #dbeafe",
    borderTop: "5px solid #2563eb",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: "18px",
  },

  // =======================================================
  // NO DATA
  // =======================================================

  noData: {
    textAlign: "center",
    fontSize: "22px",
    color: "#64748b",
    padding: "60px",
    fontWeight: "700",
  },
};
